import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DownloadBanner from '../components/DownloadBanner';
import HomeSections, { HomeSectionsSkeleton } from '../components/HomeSections';
import CampaignCard from '../components/CampaignCard';
import { CampaignCardSkeletonGrid } from '../components/CampaignCardSkeleton';
import SectionHeader from '../components/SectionHeader';
import LoginModal from '../components/LoginModal';
import Toast from '../components/Toast';
import { useModal } from '../hooks/useModal';
import { useAuth } from '../context/AuthContext';
import { useScraps } from '../context/ScrapContext';
import { useCampaignFeed } from '../hooks/useCampaignFeed';
import { useHomeSections } from '../hooks/useHomeSections';
import { scrapCampaign, unscrapCampaign } from '../api/campaigns';
import styles from './ListPage.module.css';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
import CampaignGrid from '../components/CampaignGrid';

export default function ListPage() {
  useDocumentMeta({ title: '체험단 모집', description: '지금 모집 중인 체험단을 한눈에. 마감임박부터 신규 등록까지.' });
  const navigate = useNavigate();
  const { isLoggedIn } = useAuth();
  const { isScrapped, scrap, unscrap } = useScraps();
  const { isOpen, open, close } = useModal();

  const [toast, setToast] = useState(null);
  // 로그인하느라 막혔던 저장을, 로그인 직후 이어서 끝내기 위해 기억해둔다
  const [pendingId, setPendingId] = useState(null);

  // Toast의 자동 닫힘 타이머가 매 렌더마다 초기화되지 않도록 참조를 고정한다.
  const dismissToast = useCallback(() => setToast(null), []);

  const { sections, loading: sectionsLoading } = useHomeSections();
  const { items, count, status, hasMore, loadMore, retry, pageSize } =
    useCampaignFeed();

  const handleBookmark = (campaignId) => {
    if (!isLoggedIn) { setPendingId(campaignId); open(); return; }
    return toggleScrap(campaignId);
  };

  // 인증 검사와 분리한다 — 로그인 직후에는 isLoggedIn이 아직 갱신되기 전일 수 있어
  // handleBookmark를 다시 부르면 모달이 또 열린다.
  const toggleScrap = async (campaignId) => {
    const campaign = items.find((c) => c.id === campaignId)
      ?? sections.flatMap((s) => s.items ?? []).find((c) => c.id === campaignId);
    if (!campaign) return;

    if (isScrapped(campaignId)) {
      unscrap(campaignId);
      setToast({ message: '저장을 해제했어요' });
      try { await unscrapCampaign(campaignId); }
      catch {
        scrap(campaign);
        setToast({ message: '잠시 후 다시 시도해주세요' });
      }
    } else {
      scrap(campaign);
      setToast({ message: '저장했어요' });
      try { await scrapCampaign(campaignId); }
      catch {
        unscrap(campaignId);
        setToast({ message: '잠시 후 다시 시도해주세요' });
      }
    }
  };

  return (
    <>
      <div className={styles.wrap}>
        <DownloadBanner />
        <div className={styles.bannerGap} />

        {/* 서버가 내려주는 홈 섹션 (마감임박 · 배너 · 최근 등록) */}
        {sectionsLoading
          ? <HomeSectionsSkeleton />
          : sections.length > 0 && (
              <HomeSections sections={sections} onBookmark={handleBookmark} isScrapped={isScrapped} />
            )}

        <section className={styles.allSection}>
          <SectionHeader title="전체 체험단" />

          {status === 'error' && (
            <div className={styles.state}>
              <p className={styles.stateText}>체험단 목록을 불러오지 못했습니다.</p>
              <button className={styles.retryBtn} onClick={retry}>다시 시도</button>
            </div>
          )}

          {status === 'ready' && count === 0 && (
            <div className={styles.state}>
              <p className={styles.stateText}>등록된 체험단이 없어요.</p>
            </div>
          )}

          <CampaignGrid>
            {status === 'loading'
              ? <CampaignCardSkeletonGrid count={pageSize / 2} />
              : items.map((c) => (
                  <CampaignCard
                    key={c.id}
                    campaign={c}
                    onBookmark={handleBookmark}
                    isScrapped={isScrapped(c.id)}
                  />
                ))}
          </CampaignGrid>

          {hasMore && (
            <div className={styles.moreRow}>
              <button className={styles.moreBtn} onClick={loadMore}>더 보기</button>
            </div>
          )}
        </section>
      </div>

      <LoginModal
        isOpen={isOpen}
        onClose={() => { close(); setPendingId(null); }}
        onSuccess={() => { if (pendingId) toggleScrap(pendingId); setPendingId(null); }}
      />

      {toast && (
        <Toast message={toast.message} onDismiss={dismissToast} />
      )}
    </>
  );
}
